import React, {
  useMemo,
  useCallback,
} from "react";

import {
  Box,
  createColumnHelper,
  DataTable,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";
import { AgeLabel } from "#app/components/age/age-label";
import { IssueViewContext } from "#app/components/issue-view";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DashboardActivityDrilldownDocument, type DashboardDrilldownActivity } from "#app/lib/data";
import {
  CHART_PALETTE,
  colorDotStyle,
  batterySegmentStyle,
} from "../lib/constants";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { useDrilldownGroups } from "../hooks/use-drilldown-groups";
import {
  DrilldownGroupTitle,
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

const BATTERY_BAR_STYLE = { height: 8 } as const;
const GROUP_PAGE_SIZE = 50;


// ── Row type ─────────────────────────────────────────────────────────────────

type ProjectRow = {
  id: string;
  _issueId: string;
  _ageUpdatesRaw: number;
  _currentAge: { date?: string | null; status?: string | null } | null;
  _lastAgeUpdateRaw: string;
  _projectCreatedRaw: string;
  project: string;
  ageUpdates: string;
  lastAgeUpdate: string;
  projectCreated: string;
  lastReason: string;
};

const ph = createColumnHelper<ProjectRow>();

function formatRelativeShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "Today";

  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 60) return minutes <= 1 ? "1m ago" : `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

function formatDateWithAge(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";

  const absolute = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${absolute} · ${formatRelativeShort(dateStr)}`;
}

// ── Per-member metadata (for group title rendering) ─────────────────────────

type MemberMeta = {
  userId: string;
  name: string;
  contactRate: string;
  reasons: { name: string; pct: number; color: string }[];
  total: number;
};

export type AgeReasonGroupSummary = {
  userId: string;
  name: string;
  totalUpdates: number;
  totalProjects: number;
  reasons: { name: string; count: number }[];
};

export type AgeReasonQueryInput = Omittable<{
  startDate: string;
  endDate: string;
  teamIds?: string[];
  memberIds?: string[];
  sortField?: string;
  sortDirection?: string;
}>;

const AgeReasonGroupTitle = React.memo(function AgeReasonGroupTitle({
  meta,
  onVisible,
}: Omittable<{
  meta: MemberMeta;
  onVisible?: () => void;
}>) {
  const visible = meta.reasons.filter((r) => r.pct > 0);

  return (
    <DrilldownGroupTitle onVisible={onVisible}>
      <Box direction="column" gap="xs">
        <Box direction="row" gap="xs" align="center">
          <DrilldownMemberTag userId={meta.userId} name={meta.name} />
          <Text size="xs" variant="muted">
            {meta.total} updates
          </Text>
        </Box>
        {visible.length > 0 && (
          <>
            <Box direction="row" gap="sm" wrap>
              {meta.reasons.map((r) => (
                <Box direction="row" key={r.name} gap="xs" align="center">
                  <Box width={6} height={6} rounded="full" style={colorDotStyle(r.color)} />
                  <Text size="xs" variant="muted">
                    {r.name} {r.pct}%
                  </Text>
                </Box>
              ))}
            </Box>
            <Box
              direction="row"
              rounded="full"
              overflow="hidden"
              style={BATTERY_BAR_STYLE}
            >
              {visible.map((r) => (
                <Box key={r.name} style={batterySegmentStyle(r.pct, r.color)} />
              ))}
            </Box>
          </>
        )}
      </Box>
    </DrilldownGroupTitle>
  );
});

// ── Props ─────────────────────────────────────────────────────────────────────

interface AgeReasonDrilldownProps {

  summaries: AgeReasonGroupSummary[];
  queryInput: AgeReasonQueryInput;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AgeReasonDrilldown({
  summaries,
  queryInput,
  sorting,
  onSortingChange,
}: AgeReasonDrilldownProps) {
  const issueViewCtx = React.useContext(IssueViewContext);
  const { registerState, getGroupProps } = useGroupStates<DashboardDrilldownActivity>();
  const summaryKeys = useMemo(() => summaries.map((s) => s.userId), [summaries]);
  const { activatedGroupKeys, activateGroup } = useDrilldownGroups(summaryKeys);

  const openIssueActivity = useCallback(
    (row: ProjectRow) => {
      if (row._issueId)
        issueViewCtx?.openIssueById(row._issueId, { initialTab: "activity" });
    },
    [issueViewCtx],
  );

  const reasonColorMap = useMemo(() => {
    const map = new Map<string, string>();
    const counts = new Map<string, number>();
    for (const summary of summaries) {
      for (const reason of summary.reasons) {
        counts.set(reason.name, (counts.get(reason.name) ?? 0) + reason.count);
      }
    }
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    for (const [i, [name]] of sorted.entries()) {
      map.set(name, at(CHART_PALETTE, i % CHART_PALETTE.length, "palette color"));
    }
    return map;
  }, [summaries]);

  const sort = sorting[0];
  const metaByKey = useMemo(() => {
    const next = new Map<string, MemberMeta>();

    for (const summary of summaries) {
      next.set(summary.userId, {
        userId: summary.userId,
        name: summary.name,
        contactRate: "—",
        reasons: summary.reasons.map((reason) => ({
          name: reason.name,
          pct: Math.round(
            (reason.count / Math.max(summary.totalProjects, 1)) * 100,
          ),
          color: reasonColorMap.get(reason.name) ?? "var(--muted-foreground)",
        })),
        total: summary.totalUpdates,
      });
    }

    return next;
  }, [summaries, reasonColorMap]);


  const getConnection = useCallback(
    (data: any) => data?.dashboardActivityDrilldown,
    [],
  );

  // The drilldown row is a CLIENT-ONLY aggregate, not a server noun: it
  // collapses and re-sorts the page in memory. It is built here, where the
  // row type is declared, never inside the data layer.
  const toRows = useCallback(
    (nodes: readonly DashboardDrilldownActivity[]) => {
      const projectMetrics = new Map<
        string,
        {
          totalUpdates: number;
          lastActivityDate: string;
          lastReason: string;
          issueName: string | null;
          issueCreationDate: string | null;
          issueAgeDate: string | null;
          issueAgeStatus: string | null;
        }
      >();

      for (const activity of nodes) {
        const metrics = projectMetrics.get(activity.issueId);
        const raw = activity.reasonName ?? activity.action;
        const resolvedReason =
          raw && raw.toLowerCase() !== "updated" ? raw : "—";

        if (!metrics) {
          projectMetrics.set(activity.issueId, {
            totalUpdates: 1,
            lastActivityDate: activity.creationDate,
            lastReason: resolvedReason,
            issueName: activity.issueName ?? null,
            issueCreationDate: activity.issueCreationDate ?? null,
            issueAgeDate: activity.issueAgeDate ?? null,
            issueAgeStatus: activity.issueAgeStatus ?? null,
          });
          continue;
        }

        metrics.totalUpdates += 1;
        if (activity.creationDate > metrics.lastActivityDate) {
          metrics.lastActivityDate = activity.creationDate;
          metrics.lastReason = resolvedReason;
          metrics.issueName = activity.issueName ?? metrics.issueName;
          metrics.issueCreationDate =
            activity.issueCreationDate ?? metrics.issueCreationDate;
          metrics.issueAgeDate = activity.issueAgeDate ?? metrics.issueAgeDate;
          metrics.issueAgeStatus =
            activity.issueAgeStatus ?? metrics.issueAgeStatus;
        }
      }

      const rows = Array.from(projectMetrics.entries()).map(([issueId, m]) => ({
        id: issueId,
        _issueId: issueId,
        _ageUpdatesRaw: m.totalUpdates,
        _currentAge: m.issueAgeDate
          ? { date: m.issueAgeDate, status: m.issueAgeStatus }
          : null,
        _lastAgeUpdateRaw: m.lastActivityDate ?? "",
        _projectCreatedRaw: m.issueCreationDate ?? "",
        project: m.issueName ?? `#${issueId}`,
        ageUpdates: String(m.totalUpdates),
        lastAgeUpdate: formatRelativeShort(m.lastActivityDate),
        projectCreated: formatDateWithAge(m.issueCreationDate || null),
        lastReason:
          m.lastReason === "—"
            ? "None Selected"
            : m.lastReason || "None Selected",
      }));

      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;

        rows.sort((a, b) => {
          let cmp = 0;

          switch (sortKey) {
            case "ageUpdates":
              cmp = a._ageUpdatesRaw - b._ageUpdatesRaw;
              break;
            case "_currentAge":
              cmp = (a._currentAge?.date ?? "").localeCompare(
                b._currentAge?.date ?? "",
              );
              break;
            case "lastAgeUpdate":
              cmp = a._lastAgeUpdateRaw.localeCompare(b._lastAgeUpdateRaw);
              break;
            case "projectCreated":
              cmp = a._projectCreatedRaw.localeCompare(b._projectCreatedRaw);
              break;
            default:
              cmp = String(a[sortKey as keyof ProjectRow] ?? "").localeCompare(
                String(b[sortKey as keyof ProjectRow] ?? ""),
              );
          }

          return cmp * dir;
        });
      } else {
        rows.sort((a, b) =>
          b._lastAgeUpdateRaw.localeCompare(a._lastAgeUpdateRaw),
        );
      }

      return rows;
    },
    [sort],
  );

  const groups = useMemo<DataTableGroupType<ProjectRow>[]>(() => {
    return summaries.map((summary) => ({
      key: summary.userId,
      label: summary.name,
      totalCount: summary.totalProjects,
      ...getGroupProps(summary.userId),
      data: toRows(getGroupProps(summary.userId).data),
    }));
  }, [summaries, getGroupProps, toRows]);

  // Custom group title with contact rate + battery chart
  const groupTitle = useCallback(
    (groupKey: string) => {
      const meta = metaByKey.get(groupKey);
      if (!meta) return null;
      return (
        <AgeReasonGroupTitle
          meta={meta}
          onVisible={() => activateGroup(groupKey)}
        />
      );
    },
    [activateGroup, metaByKey],
  );

  const tableView = useTableViewSettings("drilldown:ageReasons");

  // Columns
  const columns = useMemo<DataTableColumnDef<ProjectRow>[]>(
    () => [
      ph.hoverAction({
        accessorKey: "project",
        header: "Project",
        meta: { minWidth: 220 },
        content: (row) => (
          <DrilldownIssueLabel issueId={row._issueId} name={row.project} />
        ),
        onClickAction: (row) => openIssueActivity(row),
        actionLabel: (row) => `Open ${row.project} activity`,
      }),
      ph.accessor({
        accessorKey: "ageUpdates",
        header: "Age Updates",
        enableSorting: true,
        meta: { width: 92 },
      }),
      ph.accessor({
        accessorKey: "_currentAge",
        header: "Current Age",
        enableSorting: true,
        meta: { minWidth: 120 },
        cell: ({ row }) => {
          const age = row.original._currentAge;
          if (!age?.status || age.status === "disabled") {
            return (
              <Text size="sm" variant="muted">
                —
              </Text>
            );
          }
          return <AgeLabel age={age} size="xs" />;
        },
      }),
      ph.accessor({
        accessorKey: "lastAgeUpdate",
        header: "Last Age Update",
        enableSorting: true,
        meta: { minWidth: 120 },
      }),
      ph.accessor({
        accessorKey: "projectCreated",
        header: "Project Created",
        enableSorting: true,
        meta: { minWidth: 120 },
      }),
      ph.accessor({
        accessorKey: "lastReason",
        header: "Last Reason",
        enableSorting: true,
        meta: { minWidth: 160 },
      }),
    ],
    [openIssueActivity],
  );

  return (
    <>
      {summaries
        .filter((summary) => activatedGroupKeys.has(summary.userId))
        .map((summary) => (
          <GroupQueryProvider<DashboardDrilldownActivity>
            key={summary.userId}
            groupKey={summary.userId}
            document={DashboardActivityDrilldownDocument}
            variables={{
              input: {
                ...queryInput,
                subject: "age",
                userId: summary.userId,
              },
              limit: GROUP_PAGE_SIZE,
            }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      <DataTable
        columns={columns}
        groups={groups}
        groupTitle={groupTitle}
        getRowId={(r) => r.id}
        density="compact"
        fillColumnId="project"
        pinnedColumnIds={["project"]}
        stickyHeader
        hideGroupCount
        paddingX="md"
        sorting={sorting}
        onSortingChange={onSortingChange}
        {...tableView.tableProps}
      />
    </>
  );
}
